import { proxy } from 'valtio'
import { derive, devtools } from 'valtio/utils'
import { type ApiCallType } from '../../types/ApiCallType'
import { resetProxy } from '../../utils/proxyUtils'
import { customersProxy } from '../../proxies/customersProxy'
import { type GlobalCustomerType } from '../../types/GlobalCustomerType'
import {
  COMPANY_FIELD_REQUIRED_FOR_SUBMISSION,
  FIELDS_REQUIRED_FOR_SUBMISSION, NAME_FIELDS_REQUIRED_FOR_SUBMISSION
} from './validators'
import { FormFieldNamesEnum } from './formFieldNamesEnum'

export interface CustomerAddProxyType {
  allowSubmit: boolean
  allowUpdate: boolean
  apiCallCustomerCreate: ApiCallType
  apiCallCustomerDelete: ApiCallType
  displaySlideOver: boolean
  parentCustomer: GlobalCustomerType | null
  parentCustomerId: string | null
  displayParentSearchSection: boolean
  displayCustomerDeleteModal: boolean
  formValues: { [key in FormFieldNamesEnum]?: string | null }
  formErrors: { [key in FormFieldNamesEnum]?: string | null }
  formDirty: readonly FormFieldNamesEnum[]
  formBlurred: readonly FormFieldNamesEnum[]
  showStreetAddressOneError: boolean
  showCityError: boolean
  showCompanyNameError: boolean
  showEmailError: boolean
  showStateError: boolean
  showPostalCodeError: boolean

  showCountryCodeError: boolean
  showFirstNameError: boolean
  showLastNameError: boolean
}

const initialValues = {
  apiCallCustomerCreate: {
    error: null,
    loading: false
  },
  apiCallCustomerDelete: {
    error: null,
    loading: false
  },
  converge: {
    cardBrand: null,
    cardNumber: null,
    cardToken: null,
    cardType: null,
    expDate: null,
    sessionToken: null
  },
  displayCustomerDeleteModal: false,
  displayParentSearchSection: false,
  displaySlideOver: false,
  formBlurred: [],
  formDirty: [],
  formErrors: {},
  formValues: {},
  parentCustomer: null,
  parentCustomerId: null
}

const baseProxy = proxy(initialValues)

const showErrorFunctionGenerator = (fieldName: FormFieldNamesEnum) => (get: any) => {
  const { formDirty, formBlurred, formErrors } = get(baseProxy)
  const isDirty = (formDirty as string[]).includes(fieldName)
  const isBlurred = (formBlurred as string[]).includes(fieldName)
  const isErrored = Boolean((formErrors as { [key in FormFieldNamesEnum]?: string | null })[fieldName])
  return isDirty && isBlurred && isErrored
}

export const customerAddProxy = derive({
  allowSubmit: (get) => {
    const { formDirty, formErrors } = get(baseProxy)
    const noErrors = Object.values(formErrors).filter(x => x).length === 0
    // @ts-expect-error
    const allDirtied = FIELDS_REQUIRED_FOR_SUBMISSION.every((x: string) => formDirty.includes(x))
    // @ts-expect-error
    const optionalNameDirtied = NAME_FIELDS_REQUIRED_FOR_SUBMISSION.every((x: string) => formDirty.includes(x))
    // @ts-expect-error
    const optionalCompanyNameDirtied = COMPANY_FIELD_REQUIRED_FOR_SUBMISSION.every((x: string) => formDirty.includes(x))
    return noErrors && allDirtied && (optionalNameDirtied || optionalCompanyNameDirtied)
  },
  allowUpdate: (get) => {
    const { formErrors } = get(baseProxy)
    const noErrors = Object.values(formErrors).filter(x => x).length === 0
    return noErrors
  },
  showCityError: showErrorFunctionGenerator(FormFieldNamesEnum.City),
  showCompanyNameError: showErrorFunctionGenerator(FormFieldNamesEnum.CompanyName),
  showCountryCodeError: showErrorFunctionGenerator(FormFieldNamesEnum.CountryCode),
  showEmailError: showErrorFunctionGenerator(FormFieldNamesEnum.Email),
  showFirstNameError: showErrorFunctionGenerator(FormFieldNamesEnum.FirstName),
  showLastNameError: showErrorFunctionGenerator(FormFieldNamesEnum.LastName),
  showPostalCodeError: showErrorFunctionGenerator(FormFieldNamesEnum.PostalCode),
  showStateError: showErrorFunctionGenerator(FormFieldNamesEnum.State),
  showStreetAddressOneError: showErrorFunctionGenerator(FormFieldNamesEnum.StreetAddressOne),
  showStreetAddressTwoError: showErrorFunctionGenerator(FormFieldNamesEnum.StreetAddressTwo)
}, { proxy: baseProxy }) as CustomerAddProxyType

devtools(customerAddProxy, { name: 'customerAddProxy' })

export const resetCustomerAddProxy = (): void => {
  resetProxy(initialValues, customerAddProxy)

  // clear the selected customer property...BC
  customersProxy.selectedCustomer = null
}
